import { useMemo } from 'react';
import { SubscriptionPlanTemplate } from '@models/subscription';
import { PlanSelectionCards } from '@components/organisms/subscription';
import Alert from '@components/atoms/Alert';
import { Loading } from '@components/atoms/Loading';
import { useTranslation } from '@hooks/useTranslation';

export interface PlanStepProps {
  plans: SubscriptionPlanTemplate[];
  selectedPlanId: string;
  onSelectPlan: (planId: string) => void;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Plan selection at sign-up.
 *
 * There is deliberately no default and no preselection: the user always picks from the
 * public plans, cheapest first. Sign-up used to ignore plan configuration entirely and put
 * every new organization on whatever plan was named 'BASE', which is why adding public
 * plans appeared to do nothing.
 *
 * The single-plan case still renders the plan rather than skipping the step — a new
 * customer should see what they are getting before they get it.
 */
const PlanStep: React.FC<PlanStepProps> = ({
  plans,
  selectedPlanId,
  onSelectPlan,
  isLoading,
  isError,
}) => {
  // Sprint 28, story-143. Bucket 1 (pre-auth) — see docs/architecture.md "## Sprint 28" Decision 4.
  const tLogin = useTranslation('login');
  // Cheapest first. The backend already orders this way; sorting here keeps the guarantee
  // even if a caller hands us a differently-ordered list.
  const sortedPlans = useMemo(
    () => [...plans].sort((a, b) => a.basePriceCents - b.basePriceCents),
    [plans],
  );

  const isSinglePlan = sortedPlans.length === 1;

  if (isLoading) {
    return (
      <div className="py-8">
        <Loading size="xl" center />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="error" showIcon title={tLogin.couldNotLoadPlans}>
        <p>{tLogin.couldNotLoadPlansMessage}</p>
      </Alert>
    );
  }

  // No plan means no billing gate, so there is nothing safe to sign the user up to.
  // Failing visibly beats silently inventing a plan.
  if (sortedPlans.length === 0) {
    return (
      <Alert variant="warning" showIcon title={tLogin.noPlansAvailable}>
        <p>{tLogin.noPlansAvailableMessage}</p>
      </Alert>
    );
  }

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
        {isSinglePlan ? tLogin.yourPlan : tLogin.chooseYourPlan}
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        {isSinglePlan ? tLogin.yourPlanStartHint : tLogin.choosePlanHint}
      </p>

      <PlanSelectionCards
        plans={sortedPlans}
        selectedPlanId={selectedPlanId || null}
        onSelectPlan={onSelectPlan}
        billingPeriod="MONTHLY"
        label={isSinglePlan ? tLogin.yourPlan : tLogin.selectYourPlan}
        readOnly={isSinglePlan}
      />
    </div>
  );
};

export default PlanStep;
